import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import type { ColorLabel } from '../../queries/usePaginateColorLabels'

interface SyncProfileRelationalColorsData {
  profileId: string
  previousIds: string[]
  nextIds: string[]
}

const fetchColorLabel = async (id: string): Promise<ColorLabel> => {
  const { data } = await api.get<{ colorLabel: ColorLabel }>(`/color-labels/${id}`)
  return data.colorLabel
}

const updateColorLabelContactIds = async (
  colorLabelId: string,
  contactAccountIds: string[],
): Promise<void> => {
  await api.put(`/color-label/${colorLabelId}`, { contactAccountIds })
}

const syncProfileRelationalColors = async ({
  profileId,
  previousIds,
  nextIds,
}: SyncProfileRelationalColorsData): Promise<void> => {
  const toRemove = previousIds.filter((id) => !nextIds.includes(id))
  const toAdd = nextIds.filter((id) => !previousIds.includes(id))

  await Promise.allSettled([
    ...toRemove.map(async (id) => {
      const label = await fetchColorLabel(id)
      const contactAccountIds = (label.contactAccountIds ?? []).filter((x) => x !== profileId)
      await updateColorLabelContactIds(id, contactAccountIds)
    }),
    ...toAdd.map(async (id) => {
      const label = await fetchColorLabel(id)
      const contactAccountIds = [...(label.contactAccountIds ?? []), profileId]
      await updateColorLabelContactIds(id, contactAccountIds)
    }),
  ])
}

export const useSyncProfileRelationalColors = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, SyncProfileRelationalColorsData>({
    mutationFn: syncProfileRelationalColors,
    onSuccess: async (_, variables) => {
      const allIds = [...new Set([...variables.previousIds, ...variables.nextIds])]
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['color-labels'] }),
        queryClient.invalidateQueries({ queryKey: ['profile', variables.profileId] }),
        ...allIds.map((id) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ['color-label', id] }),
            queryClient.invalidateQueries({ queryKey: ['color-label-contacts', { id }] }),
          ]),
        ),
      ])
    },
  })
}
